import { call, takeEvery, put, select, takeLatest } from 'redux-saga/effects'
import { keyBy, cloneDeep } from 'lodash'

import StadiumRemote from '../remote/stadiumRemote'

import { PRINT_ORDER_REQUESTED, FETCH_PRINTERS_REQUESTED, fetchPrintersSucceeded, fetchPrintersFailed } from '../actions/printer'

import { getUseTaxClasses, makeGetCurrentMenu } from '../selectors/menus'
import { getDeviceSubtype, getReceiptSettings } from '../selectors/config'
import { getNetworkPrinter } from '../selectors/printer'
import { getOrderInProgress, makeGetOrder } from '../selectors/order'
import { makeGetServiceChargeLegalText, getVenueName } from '../selectors/venue'

import createPrinterData, { createRefundPrinterData, DEFAULT_PRINTERS, getPrintCount, PRINT_TYPES } from '../utils/printing'
import PeripheralBridge from '../utils/peripheralBridge'
import createDataDogLog from '../utils/datadog'

import MutationApi from '../remote/mutationApi'
import { getActiveAttendantId, getAttendantName } from '../selectors/attendant'
import { createOrderParams } from '../utils/orderUtils'
import { ToastManager } from '../utils/toastManager'
import { bridge_sendPrintStatus } from '../VNAndroidSDK/bridgeCalls/VNWebSDKDataSend'
import { setRouteAction } from '../VNRoutes/Actions'
import { centsToDollars } from '../utils/formatters'
import { getAndroidSDKVersion } from '../VNAndroidSDK/Selectors'
import { setNetworkAvailable } from '../actions/config'
import { isNetworkError } from '../VNUtil/Errors'

/**
 * Cloud Printing is done via a "print" order mutatation
 * we define the number of prints and template of receipt using "printType"
 *
 * @param {object} params
 * @returns {void}
 */
const printToCloud = function* (params, redirectionRoute) {
  try {
    const result = yield call(MutationApi.printOrder, params)
    const currentMutation = result?.data?.orderMutations?.[0]

    if (!currentMutation?.applied) {
      throw new Error(currentMutation?.error_message)
    }

    ToastManager.info('Receipt print sent to printer.')
  } catch (err) {
    createDataDogLog('error', `CLOUD PRINT ERROR: ${err.message ?? 'error printing receipt'}`, { params })
    bridge_sendPrintStatus(true, 'There was an error printing the receipt.')
    ToastManager.error('There was an error printing the receipt.')
  }

  if (redirectionRoute) {
    yield put(setRouteAction(redirectionRoute))
  }
}

/**
 * USB Printing is done via Android bridge call "printReceipt"
 * For printing multiple of the same auth, we must call it multiple times
 *
 * @param {object} param
 * @param {object} [params.order] - entire order payload
 * @param {string} [params.orderUuid]
 * @param {string} [params.deviceType] - 'concession' or 'kiosk'
 * @param {string} [params.printType] - type of receipt
 * @param {string} [params.redirectionRoute] - route to redirect to after printing
 * @param {boolean} [params.isAutoPrint] - whether the print is auto or manual
 * @returns {void}
 */
const printToUSB = function* ({ order, orderUuid, deviceType, printType, redirectionRoute, isAutoPrint }) {
  const getServiceChargeLegalText = makeGetServiceChargeLegalText()

  const menu = yield select((state) => makeGetCurrentMenu()(state, order.standMenuUuid))
  const serviceChargeLegal = yield select((state) => getServiceChargeLegalText(state, orderUuid))
  const deviceSubtype = yield select(getDeviceSubtype)
  const venueName = yield select(getVenueName)
  const employeeId = createOrderParams(order)?.employeeId ?? order?.employeeId
  const activeAttendantId = yield select(getActiveAttendantId)
  const attendantName = yield select((state) => getAttendantName(state, employeeId ?? activeAttendantId))
  const receiptSettings = yield select((state) => getReceiptSettings(state))
  const useTaxClasses = yield select(getUseTaxClasses)
  const androidSDKVersion = yield select(getAndroidSDKVersion)

  const payload = {
    deviceSubtype,
    menu,
    order,
    serviceChargeLegal,
    venueName,
    attendantName,
    receiptType: printType,
    useTaxClasses,
    androidSDKVersion,
  }

  const createPrinterDataFunction = printType === PRINT_TYPES.REFUND ? createRefundPrinterData : createPrinterData

  const printingData = createPrinterDataFunction(payload)

  const bridgeInstance = PeripheralBridge.getBridge()

  if (!bridgeInstance) {
    if (deviceType !== 'kiosk') {
      bridge_sendPrintStatus(true, 'Print Failed: Communication could not be established')
      ToastManager.error('Print Failed: Communication could not be established')
      createDataDogLog('error', 'Print Failed: Communication could not be established')
    }

    if (redirectionRoute) {
      yield put(setRouteAction(redirectionRoute))
    }
    return
  }

  for (
    let i = 0;
    i < getPrintCount(printType, receiptSettings?.autoPrintReceipt && isAutoPrint, receiptSettings?.print2CopiesEnabled);
    i++
  ) {
    bridgeInstance.callHandler('printReceipt', printingData)
  }

  if (redirectionRoute) {
    yield put(setRouteAction(redirectionRoute))
  }
}

/**
 * Print Order Saga
 *
 * @function
 * @param {object} [params={}]
 * @param {string} [params.payload.orderUuid]
 * @param {string} [params.payload.deviceType] - 'concession'(default) or 'kiosk'
 * @param {boolean} [params.payload.isNewOrder] - where order is in state
 * @param {string} [params.payload.printType] - type of receipt
 * @yields {void}
 */
export function* printOrderSaga(params = {}) {
  const orderUuid = params?.payload?.orderUuid
  const deviceType = params?.payload?.deviceType ?? 'concessions'
  const isNewOrder = params?.payload?.isNewOrder
  const isAutoPrint = params?.payload?.isAutoPrint
  const redirectionRoute = params?.payload?.redirectionRoute
  const printType = params?.payload?.printType ?? PRINT_TYPES.RECEIPT
  const currentNetworkPrinter = yield select((state) => getNetworkPrinter(state))
  const receiptSettings = yield select((state) => getReceiptSettings(state))

  let printCount = null

  if (printType === PRINT_TYPES.RECEIPT && isAutoPrint && receiptSettings?.autoPrintReceipt) {
    printCount = 1
    if (receiptSettings?.print2CopiesEnabled) {
      printCount = 2
    }
  } else {
    printCount = null
  }

  if (isAutoPrint && !receiptSettings?.autoPrintReceipt) return

  if (!orderUuid) {
    bridge_sendPrintStatus(true, 'Print Failed')
    ToastManager.error('Required: orderUuid is missing')
    createDataDogLog('error', 'Print Failed :Required: orderUuid is missing')
    return
  }

  // Cloud Printing
  if (currentNetworkPrinter && currentNetworkPrinter !== DEFAULT_PRINTERS.USB.uuid && deviceType !== 'kiosk') {
    yield printToCloud(
      {
        orderUuid,
        printType,
        printCount: printCount,
        printerUuids: [currentNetworkPrinter],
        paymentUuid: null,
      },
      redirectionRoute,
    )
    return
  }

  // USB Printing
  const getOrder = makeGetOrder()
  const order = isNewOrder
    ? yield select((state) => getOrderInProgress(state))
    : yield select((state) => getOrder(state, orderUuid))

  //we need to clone the order or consecutive prints for a refunds and regular receipts are mixed
  const clonedOrder = cloneDeep(order)

  let containsCharityItem = false
  clonedOrder?.itemModels?.forEach((item) => {
    if (item?.type === 'DonationLineItem') {
      containsCharityItem = true
      return
    }
  })

  if (clonedOrder?.charityAmountInCents > 0 && !containsCharityItem) {
    clonedOrder?.itemModels.push({
      name: 'Donation',
      priceInCents: clonedOrder?.charityAmountInCents,
      price: centsToDollars(clonedOrder?.charityAmountInCents),
      type: 'DonationLineItem',
      quantity: 1,
      modifiers: [],
    })
  }

  yield printToUSB({ order: clonedOrder, orderUuid, deviceType, printType, redirectionRoute, isAutoPrint })
}

/**
 * @deprecated Marked for deletion. Not used.
 * "getPrinters" is now a selector and does not exist in "Remote"
 */
export function* fetchPrinters(params = {}) {
  try {
    const result = yield call(StadiumRemote.fetchPrinters)
    const byId = keyBy(result?.data?.printers, 'uuid')
    yield put(fetchPrintersSucceeded({ byId }))
  } catch (err) {
    // todo, make this a util helper adapter in case we decide to use a different network library that throws different error objects
    if (isNetworkError(err)) {
      yield put(setNetworkAvailable(false))
    }
    yield put(fetchPrintersFailed(err))
  }
}

export function* watchPrintOrder() {
  yield takeEvery(PRINT_ORDER_REQUESTED, printOrderSaga)
}

export function* watchFetchPrinters() {
  yield takeLatest(FETCH_PRINTERS_REQUESTED, fetchPrinters)
}
